// 📄 components/StepASNTest.tsx
import React, { useState } from 'react';
import { Play, RefreshCw, CheckCircle2, XCircle, Activity } from 'lucide-react';
import { AsnFormData } from '../../../../../services/commerce/integrationService';

interface StepASNTestProps {
  testShipNoticeId: string;
  generatedDeliveryDoc: string;
  idocMonitorStatus: string;
  onChange: (field: keyof AsnFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepASNTest({ testShipNoticeId, generatedDeliveryDoc, idocMonitorStatus, onChange, isInvalid }: StepASNTestProps) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'idle' | 'success' | 'error'>('idle');

  const runTestSimulation = () => {
    setTesting(true);
    setTestResult('idle');
    setTimeout(() => {
      setTesting(false);
      // Validar: ShipNotice ID debe tener al menos 8 caracteres y no ser solo dígitos simples
      const isValid = testShipNoticeId.trim().length >= 8;
      if (isValid) {
        setTestResult('success');
        onChange('idocMonitorStatus', 'Exitoso');
      } else {
        setTestResult('error');
        onChange('idocMonitorStatus', 'Con Errores');
      }
    }, 2500);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Ruta SPRO / Transacción SAP */}
      <div className="bg-teal-500/5 border border-teal-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-teal-400 uppercase tracking-widest block">Transacción SAP de Monitoreo</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-teal-300 font-bold mr-1.5">WE02 / WE05</span>
          <span>SAP Menu → Tools → ALE → ALE Administration → Monitoring → IDoc Lists (Inbound / Outbound)</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-teal-400">5. Pruebas End-to-End (WE02 / WE05)</h3>
        <p className="text-sm text-gray-400 mt-1">
          Simula el envío de un documento ASN piloto desde SAP Ariba y confirma su correcta recepción en el monitor de IDocs.
          Verifica el estado del IDoc en <code className="bg-black/30 px-1 rounded text-teal-300">WE02</code> y la creación
          de la Entrega Entrante resultante.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            ShipNotice ID de Prueba <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={testShipNoticeId}
            onChange={(e) => onChange('testShipNoticeId', e.target.value.toUpperCase())}
            placeholder="ej. ASN-20260101-0001"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500 ${
              isInvalid && !testShipNoticeId ? 'border-red-500' : 'border-white/10'
            }`}
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            ID del ASN piloto enviado desde SAP Ariba Network al CIG.
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Nro. de Entrega Generada</label>
          <input
            type="text"
            value={generatedDeliveryDoc}
            onChange={(e) => onChange('generatedDeliveryDoc', e.target.value.replace(/\D/g, ''))}
            placeholder="ej. 5200000123"
            maxLength={10}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-teal-500"
          />
          <p className="text-[10px] text-gray-500 mt-1 px-1">
            Número de la Inbound Delivery creada automáticamente en SAP (VL33N).
          </p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-400 mb-1">Estado del Monitor de IDocs (WE02)</label>
          <select
            value={idocMonitorStatus}
            onChange={(e) => onChange('idocMonitorStatus', e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-teal-500"
          >
            <option value="Pendiente">Pendiente (No enviado)</option>
            <option value="Exitoso">Exitoso — IDoc en estado 53 (Aplicación ejecutada)</option>
            <option value="Con Errores">Con Errores — IDoc en estado 51 (Error en aplicación)</option>
            <option value="En Cola">En Cola — IDoc en estado 64 (Listo para transferir)</option>
          </select>
        </div>
      </div>

      {/* Panel de simulación */}
      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-teal-400" />
            <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Simular Recepción ASN → IDoc → Entrega</span>
          </div>
          <p className="text-xs text-gray-400">
            Valida el flujo completo: Ariba Network → CIG → DESADV IDoc → Inbound Delivery automática en SAP ERP.
          </p>
        </div>

        <button
          type="button"
          onClick={runTestSimulation}
          disabled={testing}
          className="flex-shrink-0 flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-[#fff] font-medium text-xs px-5 py-2.5 rounded-lg shadow-md shadow-teal-900/30 transition-all disabled:bg-gray-600 active:scale-95"
        >
          {testing ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Simulando envío ASN...
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5" />
              Probar Transmisión ASN
            </>
          )}
        </button>
      </div>

      {/* Resultado de la simulación */}
      {testResult === 'success' && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl flex items-start gap-3 text-sm animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold block">¡Integración ASN Exitosa!</span>
            <span className="text-xs text-green-500/80">
              El ShipNotice <code className="bg-black/30 px-1 rounded">{testShipNoticeId}</code> fue recibido
              correctamente por el CIG y procesado como IDoc DESADV en SAP. Verifica la Entrega Entrante en{' '}
              <code className="bg-black/30 px-1 rounded">VL33N</code> y el estado 53 en{' '}
              <code className="bg-black/30 px-1 rounded">WE02</code>.
            </span>
          </div>
        </div>
      )}

      {testResult === 'error' && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-start gap-3 text-sm animate-fadeIn">
          <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold block">Fallo en la Simulación</span>
            <span className="text-xs text-red-500/80">
              Por favor ingresa un ShipNotice ID válido (mínimo 8 caracteres, ej: <code className="bg-black/30 px-1 rounded">ASN-20260101-0001</code>)
              para simular el envío del documento piloto desde Ariba Network.
            </span>
          </div>
        </div>
      )}

      {/* Checklist final de validación */}
      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Checklist de Validación End-to-End</span>
        <ul className="space-y-2 text-xs text-gray-400">
          {[
            { tx: 'WE02 / WE05', desc: 'IDoc DESADV en estado 53 (procesado sin errores)' },
            { tx: 'VL33N',       desc: 'Entrega Entrante creada automáticamente con las posiciones correctas' },
            { tx: 'MB51',        desc: 'Movimiento de mercancías pendiente o registrado correctamente' },
            { tx: 'VL06I',       desc: 'Entrega visible en el monitor de entregas entrantes' },
          ].map(({ tx, desc }) => (
            <li key={tx} className="flex items-start gap-2.5">
              <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-teal-400 font-bold font-mono text-[10px] flex-shrink-0 mt-0.5">{tx}</span>
              <span>{desc}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
